import * as RowNotificationHandler from './rowNotificationHandler';
import getConversationSortByProperty from '../../utils/tables/getConversationSortByProperty';
import getMailItemSortByProperty from '../../utils/tables/getMailItemSortByProperty';
import {
    isFavoritesSearchFolderScenario,
    MailFolderTableQuery,
    MailListRowDataType,
    SortColumn,
    TableQueryType,
} from 'owa-mail-list-store';
import TableView from 'owa-mail-list-store/lib/store/schema/TableView';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import * as findConversationShapeUtil from 'owa-mail-store/lib/services/utils/findConversationShapeUtil';
import NotificationPayloadBase from 'owa-service/lib/contract/NotificationPayloadBase';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import RowNotificationPayload from 'owa-service/lib/contract/RowNotificationPayload';
import SubscriptionParameters from 'owa-service/lib/contract/SubscriptionParameters';
import { trace } from 'owa-trace';
import { action, select } from 'satcheljs/lib/legacy';
import {
    lazyGetChannelId,
    lazySubscribe,
    lazyUnsubscribe,
    NotificationSubscription,
} from 'owa-notification';
import { isFolderUnderArchiveRoot } from 'owa-mail-store';
import { isFolderOfTreeType, SHARED_FOLDERS_TREE_TYPE } from 'owa-folders';
import getHashtagsToInclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToInclude';
import getHashtagsToExclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToExclude';

const tableIdToSubscriptionMap: { [id: string]: NotificationSubscription } = {};

export interface HandleRowNotificationState {
    tableView: TableView;
}

export let handleRowNotification = select<HandleRowNotificationState>({
    tableView: (notification: NotificationPayloadBase) =>
        listViewStore.tableViews.get(notification.id),
})(
    action('handleRowNotification')(function handleRowNotifications(
        notification: NotificationPayloadBase,
        state?: HandleRowNotificationState
    ): void {
        const tableView: TableView = state.tableView;

        if (!tableView) {
            // if folder is deleted and we receive a notification, which can happen when
            // there are pending notifications for this folder, we don't handle them and just return
            trace.info('Received row notification for a deleted folder');
            return;
        }

        if (!tableView.isInitialLoadComplete) {
            // Do not process the notifications until the initial table load is complete.
            // We do this because we subscribe to the table notifications first and then
            // issue a findConversation call.
            trace.info('Ignore row notifications as table has not loaded');
            return;
        }

        let rowData: MailListRowDataType;
        switch (notification.EventType) {
            case 'RowAdded':
                rowData = getRowDataFromRowNotification(
                    notification as RowNotificationPayload,
                    tableView
                );
                RowNotificationHandler.onAddRow(rowData, tableView);
                break;
            case 'RowModified':
                rowData = getRowDataFromRowNotification(
                    notification as RowNotificationPayload,
                    tableView
                );
                RowNotificationHandler.onUpdateRow(rowData, tableView);
                break;
            case 'RowDeleted':
                rowData = getRowDataFromRowNotification(
                    notification as RowNotificationPayload,
                    tableView
                );
                RowNotificationHandler.onDeleteRow(rowData, tableView);
                break;
            case 'QueryResultChanged':
            case 'Reload':
                RowNotificationHandler.onReload(tableView);
                break;
            default:
                // Ignore other type of notifications
                return;
        }
    })
);

export function unsubscribeToRowNotifications(tableId: string): void {
    const subscription: NotificationSubscription = tableIdToSubscriptionMap[tableId];
    if (subscription) {
        // unsubscribe this subscription
        lazyUnsubscribe.importAndExecute(subscription, handleRowNotification);
        delete tableIdToSubscriptionMap[tableId];
    }
}

export function subscribeToRowNotifications(tableView: TableView): void {
    // do not subscribe if we have existing subscription
    if (tableIdToSubscriptionMap[tableView.id]) {
        return;
    }

    if (
        tableView.tableQuery.type != TableQueryType.Folder &&
        tableView.tableQuery.type != TableQueryType.Group
    ) {
        throw new Error(
            'subscribeToRowNotifications should only be called for Folder or Group table type'
        );
    }

    const mailFolderTableQuery = tableView.tableQuery as MailFolderTableQuery;

    // VSO: 18945 - Add notification support for the multivalued sort
    // Till then only support date time sort
    // Also we do not subscribe to notification if the folder is under archive mailbox or shared folders
    if (
        mailFolderTableQuery.sortBy.sortColumn != SortColumn.Date ||
        isFolderUnderArchiveRoot(tableView.tableQuery.folderId) ||
        isFolderOfTreeType(tableView.tableQuery.folderId, SHARED_FOLDERS_TREE_TYPE)
    ) {
        return;
    }

    lazyGetChannelId.import().then(getChannelId => {
        const subscriptionParameters: SubscriptionParameters = {
            NotificationType: 'RowNotification',
            FolderId: getFolderIdForSubscription(tableView),
            FocusedViewFilter: mailFolderTableQuery.focusedViewFilter, // VSO 13477: Directly pass tableQuery to table loading methods
            HashtagsToInclude: getHashtagsToInclude(mailFolderTableQuery.inboxClassificationFilter),
            HashtagsToExclude: getHashtagsToExclude(mailFolderTableQuery.inboxClassificationFilter),
            IsConversation: mailFolderTableQuery.listViewType == ReactListViewType.Conversation,
            CategoryFilter: mailFolderTableQuery.categoryName,
            Filter: mailFolderTableQuery.viewFilter,
            ChannelId: getChannelId(),
            MailboxId: getMailboxIdForSubscription(tableView),
        };

        if (subscriptionParameters.IsConversation === true) {
            subscriptionParameters.ConversationShapeName = findConversationShapeUtil.getFindConversationShape(
                tableView.tableQuery.folderId,
                isFavoritesSearchFolderScenario(mailFolderTableQuery.scenarioType)
            );
            subscriptionParameters.ConversationResponseShape = findConversationShapeUtil.getFindConversationResponseShape(
                tableView.tableQuery.folderId
            );
            subscriptionParameters.SortBy = getConversationSortByProperty(mailFolderTableQuery);
        } else {
            subscriptionParameters.SortBy = getMailItemSortByProperty(mailFolderTableQuery);
        }

        const subscription: NotificationSubscription = {
            subscriptionId: tableView.id,
            requiresExplicitSubscribe: true,
            subscriptionParameters: subscriptionParameters,
        };

        // subscribe and store the subscription object
        lazySubscribe.importAndExecute(subscription, handleRowNotification);
        tableIdToSubscriptionMap[tableView.id] = subscription;
    });
}

function getFolderIdForSubscription(tableView: TableView): string {
    return tableView.tableQuery.type == TableQueryType.Folder
        ? tableView.tableQuery.folderId
        : null;
}

function getMailboxIdForSubscription(tableView: TableView): string {
    return tableView.tableQuery.type == TableQueryType.Group ? tableView.tableQuery.folderId : null;
}

function getRowDataFromRowNotification(
    notification: RowNotificationPayload,
    tableView: TableView
): MailListRowDataType {
    // Make sure the notification payload has the correct data type
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            if (notification.Conversation) {
                return notification.Conversation;
            } else {
                trace.error(
                    'Conversation should not be undefined or empty in notification payload.' +
                        notification.EventType +
                        ', ' +
                        notification.id
                );
                return null;
            }

        case ReactListViewType.Message:
            if (notification.Item) {
                return notification.Item;
            } else {
                trace.error(
                    'Item should not be undefined or empty in notification payload.' +
                        notification.EventType +
                        ', ' +
                        notification.id
                );
                return null;
            }

        default:
            trace.error(
                'rowNotificationStrategy: unsupported listViewType: ' +
                    tableView.tableQuery.listViewType
            );
            return null;
    }
}
